import React, { createContext, FC, useCallback, useContext, useMemo, useState } from 'react';
import { LiveDateTime, MaterialIcon } from '../../components';
import { useTranslation } from '../../translations';
import { useConvention, useUserSafe } from '../../utils';
import { UserAvatar } from '../../utils/UserAvatar';
import { CashierContext } from './cashiercontext';
import { PrinterModal } from './printerModal';
import { CashierSettings } from './settingsModal';
import './header.scss';

interface NfcContextType {
  nfcReaderStatus: string;
  setNfcReaderStatus: (status: string) => void;
}

const NfcContext = createContext<NfcContextType>({
  nfcReaderStatus: 'unknown',
  setNfcReaderStatus: () => {
    // Empty function for default context
  },
});

export const Header: FC = () => {
  const { ts } = useTranslation();
  const user = useUserSafe();
  const cashierSettings = useContext(CashierContext);
  const [printerModalOpen, setPrinterModalOpen] = useState(false);
  const [nfcReaderStatus, setNfcReaderStatus] = useState<string>('unknown');

  const closePrintModal = useCallback(() => {
    setPrinterModalOpen(false);
    cashierSettings.updateCashierSettings({
      enableESCHotkey: true,
    });
  }, [cashierSettings]);

  const openPrintModal = useCallback(() => {
    setPrinterModalOpen(true);
    cashierSettings.updateCashierSettings({
      enableESCHotkey: false,
    });
  }, [cashierSettings]);

  const nfcContextValue = useMemo(
    () => ({ nfcReaderStatus, setNfcReaderStatus }),
    [nfcReaderStatus, setNfcReaderStatus],
  );

  return (
    <NfcContext.Provider value={nfcContextValue}>
      <header className="cashier-header">
        <div className="cashier-header-info">
          <div className="cashier-header-title">{ts('registration_station')}</div>
          <PrinterStatus open={openPrintModal} />
          {printerModalOpen && <PrinterModal close={closePrintModal} settings={cashierSettings} />}
        </div>
        <div className="cashier-menu">
          <div className="cashier-menu-item">
            <CashierSettings />
          </div>
        </div>
        <div className="cashier-info">
          <div className="cashier-info-username">{user.username}</div>
          <div className="cashier-info-date">
            <LiveDateTime />
          </div>
        </div>
        <div className="cashier-avatar">
          <UserAvatar size="small" url={user.profilePictureUrl} />
        </div>
      </header>
    </NfcContext.Provider>
  );
};

interface PrinterStatusProps {
  readonly open: () => void;
}

const PrinterStatus: FC<PrinterStatusProps> = ({ open }) => {
  const { ts } = useTranslation();
  const context = useContext(CashierContext);
  const { state } = context;

  const { enableNfcBadges } = useConvention();

  // TODO: Make a header element for each of the printers

  return (
    <div className="cashier-printer-info" onClick={open}>
      <MaterialIcon name="arrow_drop_down" />
      {enableNfcBadges && (
        <div className="nfc-reader-status">
          <MaterialIcon name="nfc" />
          <span>{ts('cashier_settings.nfc_status', state.nfcScanner?.status || 'unknown')}</span>
        </div>
      )}
    </div>
  );
};
